import React, { useEffect } from "react";
import { makeStyles, useTheme, Tooltip } from "@material-ui/core";
import WarningOutlinedIcon from "@material-ui/icons/WarningOutlined";
import clsx from "clsx";

import IsConnected from "./IsConnected";
import Name from "./Name";
import SubType from "./SubType";
import ContentType from "./ContentType";
import Unit from "./Unit";
import FillLimit from "./FillLimit";
import FillHeight from "./FillHeight";
import Threshold from "./Threshold";
import MeasuringRange from "./MeasuringRange";
import ContentDensity from "./ContentDensity";
import GroundClearance from "./GroundClearance";
import I18nText from "../../../../../../elements/I18nText";

const useStyles = makeStyles(theme => ({
  row: {
    paddingBottom: 6
  },
  column: {
    paddingLeft: 12
  },
  color: {
    color: theme.palette.common.white,
    justifyContent: "center",
    alignItems: "center"
  },
  colorBox: {
    width: 30,
    height: 30,
    fontSize: 13,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  isConnected: {
    textAlign: "center"
  },
  fillHeightCell: {
    display: "flex",
    alignItems: "center"
  },
  fillHeightInput: {
    flex: 1,
    minWidth: 0
  },
  hintIcon: {
    marginLeft: 4,
    fontSize: 18,
    flexShrink: 0,
    color: theme.palette.warning.main,
    cursor: "help"
  }
}));

const LEVEL_CONTENT_TYPES = ["water", "special"];
const subTypeOptions = ["tank", "silo", "level"];

const LevelProbe = ({ sensor, prefix, setFieldValue }) => {
  const classes = useStyles();
  const theme = useTheme();

  useEffect(() => {
    if (!sensor.fillHeight && sensor.measuringRange) {
      setFieldValue(`${prefix}.fillHeight`, sensor.measuringRange);
    }
  }, [sensor.measuringRange]);

  useEffect(() => {
    if (sensor.unit !== "cm" && sensor.unit !== "m") {
      setFieldValue(`${prefix}.unit`, "cm");
    }
  }, []);

  const exceedsProbeRange =
    !!sensor.fillHeight &&
    !!sensor.measuringRange &&
    sensor.fillHeight > sensor.measuringRange;

  return (
    <tr className={classes.row}>
      <td className={classes.color}>
        <div
          className={classes.colorBox}
          style={{
            backgroundColor: theme.custom.colors.sensors[sensor.sequence]
          }}
        >
          {sensor.sequence}
        </div>
      </td>
      <td className={clsx(classes.isConnected, classes.column)}>
        <IsConnected
          isConnected={sensor.isConnected}
          setFieldValue={setFieldValue}
          prefix={prefix}
        />
      </td>
      <td className={classes.column}>
        <SubType prefix={prefix} options={subTypeOptions} />
      </td>
      <td className={clsx(classes.name, classes.column)}>
        <Name prefix={prefix} />
      </td>
      <td className={classes.column}>
        <ContentType
          contentType={sensor.contentType}
          setFieldValue={setFieldValue}
          allowedOptions={LEVEL_CONTENT_TYPES}
          prefix={prefix}
        />
      </td>
      <td className={classes.column}>
        <MeasuringRange prefix={prefix} />
      </td>
      <td className={classes.column}>
        <ContentDensity prefix={prefix} />
      </td>
      <td className={classes.column}>
        <div className={classes.fillHeightCell}>
          <div className={classes.fillHeightInput}>
            <FillHeight prefix={prefix} />
          </div>
          {exceedsProbeRange && (
            <Tooltip
              title={<I18nText>sensors.levelHeightExceedsProbe</I18nText>}
              arrow
            >
              <WarningOutlinedIcon className={classes.hintIcon} />
            </Tooltip>
          )}
        </div>
      </td>
      <td className={classes.column}>
        <GroundClearance prefix={prefix} />
      </td>
      <td className={classes.column}>
        <Unit prefix={prefix} options={["cm", "m"]} />
      </td>
      <td className={classes.column}>
        <FillLimit prefix={prefix} />
      </td>
      <td className={classes.column}>
        <Threshold prefix={prefix} />
      </td>
    </tr>
  );
};

export default LevelProbe;
